import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { userService } from '../../services/user.service';
import { Withdrawal, Trade, KycStatus } from '../../types';
import Badge from '../../components/common/Badge';

const formatLogDate = (dateStr: string) => {
  if (!dateStr) return 'Processing...';
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return dateStr;
  return d.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
};

const WithdrawalsSkeleton = () => (
  <div className="space-y-8 animate-pulse">
    <div className="flex justify-between items-center">
      <div className="h-10 w-48 bg-slate-800 rounded-xl"></div>
      <div className="h-12 w-40 bg-slate-800 rounded-xl"></div>
    </div>
    <div className="h-32 bg-slate-900/30 rounded-3xl border border-slate-800/50"></div>
    <div className="h-80 bg-slate-900/30 rounded-[2.5rem] border border-slate-800/50"></div>
  </div>
);

const Withdrawals: React.FC = () => {
  const [withdrawals, setWithdrawals] = useState<any[]>([]);
  const [balance, setBalance] = useState<number>(0);
  const [kycStatus, setKycStatus] = useState<KycStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [form, setForm] = useState({ amount: '', btcAddress: '' });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [message, setMessage] = useState('');
  const [kycError, setKycError] = useState(false);
  const navigate = useNavigate();

  const fetchData = async () => {
    try {
      const [withdrawResponse, kycRes, balanceRes] = await Promise.all([
        userService.getWithdrawals(),
        userService.getKycStatus(),
        userService.getBalance()
      ]);

      const withdrawList = Array.isArray(withdrawResponse?.data?.data) ? withdrawResponse.data.data : (Array.isArray(withdrawResponse?.data) ? withdrawResponse.data : (withdrawResponse || []));
      setWithdrawals(withdrawList);

      const kyc = kycRes?.data || kycRes;
      setKycStatus(kyc);

      // --- DYNAMIC BALANCE FROM API ---
      const fetchedBalance = Number(balanceRes?.data?.balance || 0);
      setBalance(fetchedBalance);

    } catch (err) {
      console.error("Transmission error:", err);
      setWithdrawals([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const getStatusString = (status: any) => {
    const s = Number(status);
    if (s === 1) return 'APPROVED';
    if (s === 2) return 'REJECTED';
    return 'PENDING';
  };

  const handleWithdraw = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setMessage('');
    setKycError(false);

    const requestedAmount = Number(form.amount);
    // Use Number check for status 0 (Pending)
    const hasPending = withdrawals.some(w => Number(w.status) === 0);
    const isKycApproved = kycStatus?.verifyStatus === "1";

    if (!isKycApproved) {
      setKycError(true);
      setMessage('Identity Audit Required. Asset distribution locked until verified.');
      setIsSubmitting(false);
      return;
    }

    if (hasPending) {
      setMessage('Protocol Lock: An operation is already pending broadcast.');
      setIsSubmitting(false);
      return;
    }

    if (requestedAmount > balance) {
      setMessage('Insufficient Dynamic Liquidity for this operation.');
      setIsSubmitting(false);
      return;
    }

    try {
      const response = await userService.createWithdraw({
        amount: requestedAmount,
        btcAddress: form.btcAddress
      });
      if (response.status) {
        setMessage('Withdrawal initialized. Broadcast in progress.');
        setForm({ amount: '', btcAddress: '' });
        setTimeout(() => {
          setShowModal(false);
          fetchData();
        }, 2000);
      } else {
        throw new Error(response.message);
      }
    } catch (err: any) {
      setMessage(err.message || 'Network handshake failed.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (loading) return <WithdrawalsSkeleton />;

  const hasPendingWithdrawal = withdrawals.some(w => Number(w.status) === 0);

  return (
    <div className="space-y-10 animate-in fade-in slide-in-from-bottom-4 duration-700 pb-20">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-6">
        <div>
          <h2 className="text-3xl font-black text-white uppercase tracking-tight">Withdrawal Network</h2>
          <p className="text-slate-500 text-[10px] font-black uppercase tracking-[0.3em] mt-1">Global Asset Distribution History</p>
        </div>
        <button 
          onClick={() => {
            fetchData();
            setShowModal(true);
          }} 
          className="bg-purple-600 hover:bg-purple-500 text-white font-black px-10 py-5 rounded-[1.5rem] transition-all shadow-2xl shadow-purple-600/40 active:scale-95 border border-purple-400/20 uppercase tracking-[0.1em] text-xs"
        >
          Initiate Transfer
        </button>
      </div>

      <div className="relative overflow-hidden group">
        <div className="absolute inset-0 bg-gradient-to-r from-purple-600/10 to-transparent rounded-[2.5rem]"></div>
        <div className="relative glass-panel rounded-[2.5rem] p-10 border-purple-500/20 flex flex-col md:flex-row md:items-center justify-between gap-8">
           <div className="flex items-center space-x-6">
              <div className="w-16 h-16 bg-purple-500/10 rounded-2xl flex items-center justify-center border border-purple-500/20 shadow-inner">
                 <svg className="w-8 h-8 text-purple-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 9V7a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2m2 4h10a2 2 0 002-2v-6a2 2 0 00-2-2H9a2 2 0 00-2 2v6a2 2 0 002 2zm7-5a2 2 0 11-4 0 2 2 0 014 0z" /></svg>
              </div>
              <div>
                 <p className="text-[10px] font-black text-slate-500 uppercase tracking-[0.3em]">Recalculated Liquidity</p>
                 <h3 className="text-4xl font-black text-white tracking-tighter mt-1">${balance.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</h3>
              </div>
           </div>
           <div className="bg-slate-950/40 border border-white/5 rounded-2xl px-6 py-4 backdrop-blur-md">
              <div className="flex items-center space-x-2">
                 <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse"></span>
                 <p className="text-[9px] font-black text-slate-400 uppercase tracking-widest">Protocol Check: Passed</p>
              </div>
              <p className="text-[8px] text-slate-600 mt-1 uppercase">Dynamic balance derived directly from core node ledger</p>
           </div>
        </div>
      </div>

      <div className="bg-slate-900/30 backdrop-blur-xl rounded-[2.5rem] border border-slate-800/50 overflow-hidden shadow-2xl">
        <div className="px-10 py-6 border-b border-white/5 bg-slate-900/60">
           <h4 className="text-sm font-black text-white uppercase tracking-widest">Broadcast Logs</h4>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-left">
            <thead className="bg-slate-900/80 text-slate-500 uppercase text-[10px] tracking-widest font-black border-b border-slate-800/50">
              <tr>
                <th className="px-10 py-6">Transfer ID</th>
                <th className="px-10 py-6">Value (USD)</th>
                <th className="px-10 py-6">Destination Address</th>
                <th className="px-10 py-6">Status</th>
                <th className="px-10 py-6">Broadcast Time</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-800/30">
              {withdrawals.length > 0 ? (
                withdrawals.map((w) => {
                  const statusNum = Number(w.status);
                  const statusStr = getStatusString(statusNum);
                  const isApproved = statusNum === 1;
                  const isRejected = statusNum === 2;
                  const isPending = statusNum === 0;
                  
                  return (
                    <tr key={w.wdrId || w.id} className="hover:bg-purple-600/5 transition-colors group">
                      <td className="px-10 py-6 text-xs font-mono text-slate-500 group-hover:text-slate-300">TX-{w.wdrId || w.id}</td>
                      <td className="px-10 py-6 text-sm font-black text-white">${Number(w.amount || 0).toFixed(2)}</td>
                      <td className="px-10 py-6 text-xs font-mono text-slate-400 max-w-[200px] truncate">
                        {w.btcAddress}
                        {isRejected && w.rejectionReason && (
                           <p className="text-[10px] text-rose-500 font-bold uppercase mt-1 italic">Reason: {w.rejectionReason}</p>
                        )}
                      </td>
                      <td className="px-10 py-6">
                        <Badge label={statusStr} variant={isApproved ? 'success' : isRejected ? 'error' : 'warning'} />
                      </td>
                      <td className="px-10 py-6 text-xs text-slate-500 font-bold">
                        {isApproved ? formatLogDate(w.updatedAt || w.createdAt) : isPending ? 'Awaiting Broadcast...' : 'REJECTED'}
                      </td>
                    </tr>
                  );
                })
              ) : (
                <tr>
                  <td colSpan={5} className="px-10 py-32 text-center text-slate-500 uppercase text-[10px] font-black tracking-[0.5em]">
                    <div className="flex flex-col items-center opacity-30">
                      <svg className="w-16 h-16 mb-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" /></svg>
                      No broadcast data found
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {showModal && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-slate-950/90 backdrop-blur-md">
          <div className="bg-slate-900 border border-slate-800 w-full max-w-lg p-10 rounded-[2.5rem] shadow-2xl animate-in zoom-in-95 duration-300 relative overflow-hidden text-left">
            <div className="absolute -top-24 -right-24 w-48 h-48 bg-purple-600/10 rounded-full blur-3xl"></div>
            <h3 className="text-2xl font-black text-white uppercase tracking-tight relative z-10">Transfer Terminal</h3>
            <p className="text-slate-500 text-[10px] font-black uppercase tracking-[0.3em] mt-2 mb-8 relative z-10">Define your asset destination</p>
            <form onSubmit={handleWithdraw} className="space-y-6 relative z-10">
              <div className="bg-slate-950/50 p-4 rounded-2xl border border-white/5 mb-2">
                 <p className="text-[9px] font-black text-slate-600 uppercase tracking-widest">Available Balance</p>
                 <p className="text-sm font-black text-purple-400 mt-0.5">${balance.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} USDT</p>
              </div>

              {hasPendingWithdrawal && (
                <div className="bg-amber-500/10 border border-amber-500/20 p-4 rounded-xl text-[10px] font-black text-amber-500 uppercase tracking-widest">
                  Protocol Alert: Broadcast node is busy with a pending TX.
                </div>
              )}

              {kycStatus?.verifyStatus !== "1" && (
                <div className="bg-rose-500/10 border border-rose-500/20 p-4 rounded-xl text-[10px] font-black text-rose-500 uppercase tracking-widest">
                  Identity Failure: Verifcation required for distribution.
                </div>
              )}

              <div>
                <label className="block text-[10px] font-black text-slate-500 uppercase tracking-widest ml-2 mb-3">Quantity (USD)</label>
                <input 
                  type="number" 
                  required 
                  max={balance} 
                  disabled={hasPendingWithdrawal || kycStatus?.verifyStatus !== "1"}
                  value={form.amount} 
                  onChange={e => setForm({...form, amount: e.target.value})} 
                  className="w-full bg-slate-950 border border-slate-800 rounded-2xl px-6 py-5 text-white outline-none focus:ring-2 focus:ring-purple-600/40 transition-all font-bold placeholder-slate-800 shadow-inner disabled:opacity-30" 
                  placeholder="0.00" 
                />
              </div>
              <div>
                <label className="block text-[10px] font-black text-slate-500 uppercase tracking-widest ml-2 mb-3">Protocol Address (BTC/USDT)</label>
                <input 
                  type="text" 
                  required 
                  disabled={hasPendingWithdrawal || kycStatus?.verifyStatus !== "1"}
                  value={form.btcAddress} 
                  onChange={e => setForm({...form, btcAddress: e.target.value})} 
                  className="w-full bg-slate-950 border border-slate-800 rounded-2xl px-6 py-5 text-white font-mono text-sm outline-none focus:ring-2 focus:ring-purple-600/40 transition-all placeholder-slate-800 shadow-inner disabled:opacity-30" 
                  placeholder="Network Hash Address" 
                />
              </div>
              {message && (
                <div className={`p-5 text-[11px] font-black uppercase rounded-2xl text-center border animate-in slide-in-from-top-2 ${kycError ? 'bg-amber-500/10 text-amber-500 border-amber-500/20' : message.includes('failed') || message.includes('Insufficient') || message.includes('Already') ? 'bg-rose-500/10 text-rose-500 border-rose-500/20' : 'bg-purple-500/10 text-purple-400 border-purple-500/20'}`}>
                  {message}
                  {kycError && <button type="button" onClick={() => navigate('/kyc')} className="block w-full mt-4 bg-amber-500 text-slate-950 py-3 rounded-xl font-black transition-transform active:scale-95">Verify Account Now</button>}
                </div>
              )}
              <div className="flex space-x-4 pt-6">
                <button type="button" onClick={() => setShowModal(false)} className="flex-1 bg-slate-800 text-slate-400 font-bold py-5 rounded-2xl hover:bg-slate-700 transition-all uppercase text-[10px] tracking-widest">Abort</button>
                <button 
                  type="submit" 
                  disabled={isSubmitting || !form.amount || Number(form.amount) > balance || hasPendingWithdrawal || kycStatus?.verifyStatus !== "1"} 
                  className="flex-1 bg-purple-600 text-white font-black py-5 rounded-2xl hover:bg-purple-500 transition-all shadow-xl shadow-purple-600/30 disabled:opacity-30 uppercase text-[10px] tracking-widest"
                >
                  {isSubmitting ? 'Syncing...' : 'Confirm Transfer'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Withdrawals;